import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  getListings,
  addListing,
  updateListing,
  deleteListing,
  updatePublishStatus,
} from "../../../Api/residenceListingApi";
import { getResidenceSubtypes } from "../../../Api/residenceApi";
import { toast } from "react-toastify";
import { FaPlus } from "react-icons/fa";

function MyListings() {
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  })();
  const residentId = storedUser?.id || storedUser?.resident_id || null;

  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    residence_subtype_id: "",
    listing_type: "sell",
    size: "",
    contact_info: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const listingsPerPage = 6;

  const filteredByType = filteredListings.filter((l) =>
    filterType === "all" ? true : l.listing_type === filterType
  );

  const searchedListings = filteredByType.filter((l) =>
    searchQuery
      ? l.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        l.description?.toLowerCase().includes(searchQuery.toLowerCase())
      : true
  );

  const totalPages = Math.ceil(searchedListings.length / listingsPerPage);
  const paginatedListings = searchedListings.slice(
    (currentPage - 1) * listingsPerPage,
    currentPage * listingsPerPage
  );

  useEffect(() => {
    if (!residentId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const resListings = await getListings();
        const listingsData = Array.isArray(resListings.data)
          ? resListings.data
          : resListings.data?.data || [];
        setListings(listingsData);
        setFilteredListings(listingsData);

        const resSubtypes = await getResidenceSubtypes();
        const subtypesData = Array.isArray(resSubtypes.data)
          ? resSubtypes.data
          : [];
        setSubtypes(subtypesData);
      } catch {
        // Silent fail in production: reset state
        setListings([]);
        setFilteredListings([]);
        setSubtypes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [residentId]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.residence_subtype_id)
      newErrors.residence_subtype_id = "Please select a subtype";
    if (!formData.listing_type)
      newErrors.listing_type = "Listing type is required";
    if (!formData.size.trim()) newErrors.size = "Size is required";
    else if (isNaN(Number(formData.size)))
      newErrors.size = "Size must be a number";
    if (!formData.contact_info.trim())
      newErrors.contact_info = "Contact info is required";

    const duplicate = listings.find(
      (l) =>
        l.title.toLowerCase() === formData.title.trim().toLowerCase() &&
        l.listing_id !== editingItem?.listing_id
    );
    if (duplicate) newErrors.title = "A listing with this title already exists";

    return newErrors;
  };

  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const payload = { ...formData, resident_id: residentId };
      const res =
        editingItem !== null
          ? await updateListing(editingItem.listing_id, payload)
          : await addListing(payload);

      const updatedListing = res.data.data || payload;
      const updatedListings = editingItem
        ? listings.map((l) =>
            l.listing_id === editingItem.listing_id ? updatedListing : l
          )
        : [updatedListing, ...listings];

      setListings(updatedListings);
      setFilteredListings(updatedListings);
      closeModal();
      toast.success(editingItem ? "Listing updated!" : "Listing added!");
    } catch (err) {
      toast.error("Failed to save listing.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      title: "",
      description: "",
      residence_subtype_id: "",
      listing_type: "sell",
      size: "",
      contact_info: "",
    });
    setErrors({});
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || "",
      residence_subtype_id: item.residence_subtype_id,
      listing_type: item.listing_type,
      size: item.size || "",
      contact_info: item.contact_info || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (listing_id) => {
    if (!window.confirm("Are you sure you want to delete this listing?"))
      return;
    try {
      await deleteListing(listing_id);
      const updated = listings.filter((l) => l.listing_id !== listing_id);
      setListings(updated);
      setFilteredListings(updated);
      toast.success("Listing deleted successfully.");
    } catch (err) {
      toast.error("Failed to delete listing.");
    }
  };

  const handleTogglePublish = async (item) => {
    try {
      const res = await updatePublishStatus(
        item.listing_id,
        item.is_published === 1 ? 0 : 1
      );
      const updated = listings.map((l) =>
        l.listing_id === item.listing_id
          ? { ...l, is_published: res.data.is_published }
          : l
      );
      setListings(updated);
      setFilteredListings(updated);
      toast.success(
        `Listing ${
          res.data.is_published ? "published" : "unpublished"
        } successfully.`
      );
    } catch (err) {
      toast.error("Failed to update publish status.");
    }
  };

  // Simplified badge logic
  const badgeMap = {
    sell: { label: "For Sell", color: "bg-Dark text-white" },
    rent: { label: "For Rent", color: "bg-Light text-gray-900" },
  };

  return (
    <div
      className="min-h-screen bg-gray-50 px-4 sm:px-6 py-12 sm:py-16 font-Poppins 
                    dark:bg-gray-900 dark:text-gray-200 transition-colors duration-300"
    >
      <Helmet>
        <title>My Listings – CCD Gated Community</title>
      </Helmet>

      {/* Search + Filter + Add */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6 mt-6 px-4 sm:px-6 lg:px-12 max-w-6xl mx-auto">
        {/* Search Input */}
        <input
          type="text"
          placeholder="Search listings..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full sm:flex-1 border border-gray-300 rounded-md px-5 py-2.5 focus:outline-none focus:ring-1 focus:ring-Dark placeholder-gray-400 text-gray-700
                     dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 dark:placeholder-gray-400"
        />

        {/* Filter Select */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-Dark
                     dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200"
        >
          <option value="all">All</option>
          <option value="sell">Sell</option>
          <option value="rent">Rent</option>
        </select>

        {/* Add Listing Button (Accent colors retained) */}
        <button
          onClick={() => setShowModal(true)}
          className="bg-Dark text-white px-6 py-2.5 rounded-md hover:bg-Dark/90 transition font-semibold flex items-center gap-2 mt-2 sm:mt-0"
        >
          <FaPlus /> <span>Add Listing</span>
        </button>
      </div>

      {/* Listings Grid */}
      {loading ? (
        <p className="text-gray-500 text-center dark:text-gray-400">
          Loading listings...
        </p>
      ) : paginatedListings.length === 0 ? (
        <p className="text-gray-600 text-center mt-12 dark:text-gray-400">
          No listings found.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {paginatedListings.map((l) => {
            const badge = badgeMap[l.listing_type] || {
              label: l.listing_type.toUpperCase(),
              color: "bg-gray-400 text-white",
            };
            return (
              <div
                key={l.listing_id}
                className="bg-white rounded-xl border border-gray-200 p-4 shadow hover:shadow-lg transition relative flex flex-col
                           dark:bg-gray-800 dark:border-gray-700 dark:shadow-xl dark:hover:shadow-2xl"
              >
                <div
                  className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}
                >
                  {badge.label}
                </div>

                <h3 className="text-lg font-bold text-Dark mt-6">{l.title}</h3>
                <p className="text-gray-600 text-sm dark:text-gray-300">
                  <span className="font-medium text-Dark">Subtype:</span>{" "}
                  {subtypes.find((s) => s.id === l.residence_subtype_id)
                    ?.name || "N/A"}
                </p>
                <p className="text-gray-600 text-sm dark:text-gray-300">
                  <span className="font-medium text-Dark">Size:</span>{" "}
                  {l.size ? `${l.size} sqm` : "N/A"}
                </p>
                {l.description && (
                  <p className="text-gray-500 text-sm line-clamp-3 mt-1 dark:text-gray-400">
                    {l.description}
                  </p>
                )}
                <p className="text-gray-600 text-xs mt-1 dark:text-gray-300">
                  <span className="font-medium text-Dark">Contact:</span>{" "}
                  {l.contact_info || "N/A"}
                </p>

                {/* Resident Controls */}
                <div
                  className="border-t border-gray-200 p-3 flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-50 mt-3 rounded-b-xl
                                dark:border-gray-700 dark:bg-gray-900"
                >
                  <label className="flex items-center gap-2 text-sm cursor-pointer dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={l.is_published === 1}
                      onChange={() => handleTogglePublish(l)}
                      className="accent-Dark w-4 h-4 cursor-pointer"
                    />
                    <span
                      className={`font-medium ${
                        l.is_published ? "text-Light" : "text-Dark"
                      }`}
                    >
                      {l.is_published ? "Published" : "Unpublished"}
                    </span>
                  </label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleEdit(l)}
                      className="text-blue-600 text-sm font-medium hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(l.listing_id)}
                      className="text-red-500 text-sm font-medium hover:underline dark:text-red-400 dark:hover:text-red-300"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8 mb-8">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            className="px-3 py-1 border rounded-md hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800 dark:text-gray-300"
            disabled={currentPage === 1}
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 border rounded-md 
                ${
                  page === currentPage
                    ? "bg-Dark text-white"
                    : "hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800 dark:text-gray-300"
                }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            className="px-3 py-1 border rounded-md hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800 dark:text-gray-300"
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 relative
                        dark:bg-gray-800 dark:shadow-2xl"
          >
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              onClick={closeModal}
            >
              ✕
            </button>
            <h2 className="text-lg font-bold mb-4 text-Dark">
              {editingItem ? "Edit Listing" : "Add Listing"}
            </h2>
            <form className="space-y-4" onSubmit={handleAddOrUpdate}>
              {/* Title */}
              <div className="flex flex-col">
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleFormChange("title", e.target.value)}
                  placeholder="Listing Title"
                  className={`w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-Dark 
                              dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-200
                              ${errors.title ? "border-red-500" : ""}`}
                />
                {errors.title && (
                  <p className="text-red-500 text-xs mt-1">{errors.title}</p>
                )}
              </div>

              {/* Description */}
              <div className="flex flex-col">
                <textarea
                  rows="4"
                  value={formData.description}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  placeholder="Description (optional)"
                  className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-Dark
                             dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-200"
                />
              </div>

              {/* Subtype */}
              <div className="flex flex-col">
                <select
                  value={formData.residence_subtype_id}
                  onChange={(e) =>
                    handleFormChange("residence_subtype_id", e.target.value)
                  }
                  className={`w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-Dark
                              dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200
                              ${
                                errors.residence_subtype_id
                                  ? "border-red-500"
                                  : ""
                              }`}
                >
                  <option value="">Select Subtype</option>
                  {subtypes.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {errors.residence_subtype_id && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.residence_subtype_id}
                  </p>
                )}
              </div>

              {/* Listing Type & Size */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex flex-col flex-1">
                  <select
                    value={formData.listing_type}
                    onChange={(e) =>
                      handleFormChange("listing_type", e.target.value)
                    }
                    className={`border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-Dark 
                                dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200
                                ${errors.listing_type ? "border-red-500" : ""}`}
                  >
                    <option value="sell">Sell</option>
                    <option value="rent">Rent</option>
                  </select>
                  {errors.listing_type && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.listing_type}
                    </p>
                  )}
                </div>

                <div className="flex flex-col flex-1">
                  <input
                    type="text"
                    placeholder="Size (sqm)"
                    value={formData.size}
                    onChange={(e) => handleFormChange("size", e.target.value)}
                    className={`border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-Dark 
                                dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-200
                                ${errors.size ? "border-red-500" : ""}`}
                  />
                  {errors.size && (
                    <p className="text-red-500 text-xs mt-1">{errors.size}</p>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="flex flex-col">
                <input
                  type="text"
                  value={formData.contact_info}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d+]/g, "");
                    if (!value.startsWith("+251"))
                      value = "+251" + value.replace(/^(\+251)?/, "");
                    if (value.length > 13) value = value.slice(0, 13);

                    handleFormChange("contact_info", value);

                    const phonePattern = /^\+251[0-9]{9}$/;
                    setErrors((prev) => ({
                      ...prev,
                      contact_info: !phonePattern.test(value)
                        ? "Phone must start with +251 and be 13 characters."
                        : "",
                    }));
                  }}
                  placeholder="Phone (+251)"
                  className={`w-full border p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-Dark 
                              dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-200
                              ${
                                errors.contact_info
                                  ? "border-red-500"
                                  : "border-gray-300"
                              }`}
                />
                {errors.contact_info && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.contact_info}
                  </p>
                )}
              </div>

              {/* Submit Button (Accent colors retained) */}
              <button
                type="submit"
                disabled={submitting}
                className="bg-Dark text-white w-full py-3 rounded-md hover:bg-Dark/90 transition font-semibold"
              >
                {submitting
                  ? "Saving..."
                  : editingItem
                  ? "Update Listing"
                  : "Add Listing"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyListings;
